import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import AppLayout from "../components/AppLayout";
import StorageImage from "../components/StorageImage";
import { formatMoney as formatCurrency } from "../lib/currency";
import { supabase } from "../lib/supabase";
import { buildStorageReference } from "../lib/storageReferences";
import { useToast } from "../contexts/ToastContext";
import { useAuth } from "../contexts/AuthContext";
import { motion } from "framer-motion";
import {
  ArrowUpRight,
  Building2,
  Filter,
  Grid3X3,
  List,
  MapPin,
  Plus,
  Search,
  Users,
  Wallet,
  Wrench,
  X,
  Pencil,
  Trash2,
  Image,
} from "lucide-react";

function Button({ children, className = "", variant = "default", ...props }) {
  const base =
    "inline-flex items-center justify-center whitespace-nowrap transition-all font-medium disabled:pointer-events-none disabled:opacity-50";
  const styles =
    variant === "outline"
      ? "border"
      : "bg-orange-500 text-white hover:bg-orange-400 shadow-sm";

  return (
    <button className={`${base} ${styles} ${className}`} {...props}>
      {children}
    </button>
  );
}

function Input({ className = "", ...props }) {
  return (
    <input
      {...props}
      className={`flex w-full rounded-2xl border px-4 py-3 text-sm outline-none transition focus:border-sky-300 focus:ring-4 focus:ring-sky-100 ${className}`}
    />
  );
}

function Select({ className = "", children, ...props }) {
  return (
    <select
      {...props}
      className={`flex w-full rounded-2xl border px-4 py-3 text-sm outline-none transition focus:border-sky-300 focus:ring-4 focus:ring-sky-100 ${className}`}
    >
      {children}
    </select>
  );
}

function Badge({ children, className = "" }) {
  return (
    <span className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-medium ${className}`}>
      {children}
    </span>
  );
}

function formatMoney(amount, currency) {
  return formatCurrency(amount, currency);
}

function getStatusClasses(status) {
  if (status === "High performing") return "bg-emerald-100 text-emerald-700";
  if (status === "Stable") return "bg-sky-100 text-sky-700";
  return "bg-amber-100 text-amber-800";
}

function extractOrganizationId(profile, user, sessionClaims) {
  return (
    profile?.organization_id ||
    user?.app_metadata?.organization_id ||
    user?.app_metadata?.organizationId ||
    user?.user_metadata?.organization_id ||
    user?.user_metadata?.organizationId ||
    sessionClaims?.app_metadata?.organization_id ||
    sessionClaims?.app_metadata?.organizationId ||
    sessionClaims?.user_metadata?.organization_id ||
    sessionClaims?.user_metadata?.organizationId ||
    ""
  );
}

const PROPERTY_IMAGE_BUCKET = "property-images";

function getPropertyImagePath(propertyId, file) {
  const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, "-");
  const owner = propertyId || "new-property";

  return `${owner}/${Date.now()}-${safeName}`;
}

async function uploadPropertyImage(propertyId, file) {
  const path = getPropertyImagePath(propertyId, file);
  const { error } = await supabase.storage
    .from(PROPERTY_IMAGE_BUCKET)
    .upload(path, file, {
      cacheControl: "3600",
      upsert: false,
    });

  if (error) throw error;

  return buildStorageReference(PROPERTY_IMAGE_BUCKET, path);
}

function PropertyFormModal({
  isOpen,
  mode = "create",
  property = null,
  onClose,
  onSaved,
  isDark,
  currency,
  currentOrganizationId,
}) {
  const toast = useToast();
  const [form, setForm] = useState({
    name: "",
    location: "",
    type: "Apartments",
    units: "",
    occupied: "",
    rent_per_unit: "",
    maintenance_open: "",
    manager: "",
    property_image_url: "",
    status: "Stable",
  });

  const [saving, setSaving] = useState(false);
  const [propertyImageFile, setPropertyImageFile] = useState(null);

  useEffect(() => {
    if (property) {
      setForm({
        name: property.name || "",
        location: property.location || "",
        type: property.type || "Apartments",
        units: property.units ?? "",
        occupied: property.occupied ?? "",
        rent_per_unit: property.rent_per_unit ?? "",
        maintenance_open: property.maintenance_open ?? "",
        manager: property.manager || "",
        property_image_url: property.property_image_url || "",
        status: property.status || "Stable",
      });
    } else {
      setForm({
        name: "",
        location: "",
        type: "Apartments",
        units: "",
        occupied: "",
        rent_per_unit: "",
        maintenance_open: "",
        manager: "",
        property_image_url: "",
        status: "Stable",
      });
    }

    setPropertyImageFile(null);
  }, [property, isOpen]);

  if (!isOpen) return null;

  const update = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    let propertyImageUrl = form.property_image_url;

    try {
      if (propertyImageFile) {
        propertyImageUrl = await uploadPropertyImage(property?.id, propertyImageFile);
      }
    } catch (error) {
      setSaving(false);
      console.error("Property image upload error:", error);
      toast.error(
        "Property image upload failed",
        "Please check your storage bucket configuration and try again."
      );
      return;
    }

    const payload = {
      name: form.name,
      location: form.location,
      type: form.type,
      units: Number(form.units || 0),
      occupied: Number(form.occupied || 0),
      rent_per_unit: Number(form.rent_per_unit || 0),
      maintenance_open: Number(form.maintenance_open || 0),
      manager: form.manager,
      property_image_url: propertyImageUrl,
      status: form.status,
      organization_id: property?.organization_id || currentOrganizationId || null,
    };

    let error;

    if (mode === "edit" && property?.id) {
      const result = await supabase
        .from("properties")
        .update(payload)
        .eq("id", property.id);
      error = result.error;
    } else {
      const result = await supabase.from("properties").insert([payload]);
      error = result.error;
    }

    setSaving(false);

    if (error) {
      console.error(`${mode} error:`, error);
      toast.error(
        mode === "edit" ? "Property update failed" : "Property creation failed",
        "Please review the property details and try again."
      );
      return;
    }

    toast.success(
      mode === "edit" ? "Property updated" : "Property created",
      `${form.name} is now saved in SpaceM8.`
    );
    onSaved();
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div
        className={`w-full max-w-2xl rounded-[2rem] border p-6 shadow-2xl ${
          isDark ? "border-slate-800 bg-slate-900" : "border-slate-200 bg-white"
        }`}
      >
        <div className="mb-6 flex items-center justify-between">
          <div>
            <h2 className={`text-2xl font-semibold ${isDark ? "text-white" : "text-slate-900"}`}>
              {mode === "edit" ? "Edit Property" : "Add Property"}
            </h2>
            <p className={`mt-1 text-sm ${isDark ? "text-slate-400" : "text-slate-500"}`}>
              {mode === "edit"
                ? "Update this property record."
                : "Create a new property record in SpaceM8."}
            </p>
          </div>

          <button
            onClick={onClose}
            className={`rounded-2xl border p-3 transition ${
              isDark
                ? "border-slate-700 bg-slate-900 text-white hover:bg-slate-800"
                : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50"
            }`}
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="grid gap-4 md:grid-cols-2">
          <Input
            placeholder="Property name"
            value={form.name}
            onChange={(e) => update("name", e.target.value)}
            className={
              isDark
                ? "border-slate-700 bg-slate-950 text-white placeholder:text-slate-500"
                : "border-slate-200 bg-white text-slate-900"
            }
            required
          />

          <Input
            placeholder="Location"
            value={form.location}
            onChange={(e) => update("location", e.target.value)}
            className={
              isDark
                ? "border-slate-700 bg-slate-950 text-white placeholder:text-slate-500"
                : "border-slate-200 bg-white text-slate-900"
            }
            required
          />

          <Select
            value={form.type}
            onChange={(e) => update("type", e.target.value)}
            className={
              isDark
                ? "border-slate-700 bg-slate-950 text-white"
                : "border-slate-200 bg-white text-slate-900"
            }
          >
            <option>Apartments</option>
            <option>Serviced Suites</option>
            <option>Townhouses</option>
            <option>Mixed Use</option>
            <option>Lofts</option>
          </Select>

          <Select
            value={form.status}
            onChange={(e) => update("status", e.target.value)}
            className={
              isDark
                ? "border-slate-700 bg-slate-950 text-white"
                : "border-slate-200 bg-white text-slate-900"
            }
          >
            <option>High performing</option>
            <option>Stable</option>
            <option>Needs attention</option>
          </Select>

          <Input
            type="number"
            placeholder="Total units"
            value={form.units}
            onChange={(e) => update("units", e.target.value)}
            className={
              isDark
                ? "border-slate-700 bg-slate-950 text-white placeholder:text-slate-500"
                : "border-slate-200 bg-white text-slate-900"
            }
            required
          />

          <Input
            type="number"
            placeholder="Occupied units"
            value={form.occupied}
            onChange={(e) => update("occupied", e.target.value)}
            className={
              isDark
                ? "border-slate-700 bg-slate-950 text-white placeholder:text-slate-500"
                : "border-slate-200 bg-white text-slate-900"
            }
            required
          />

          <Input
            type="number"
            placeholder={`Rent per occupied unit (${currency})`}
            value={form.rent_per_unit}
            onChange={(e) => update("rent_per_unit", e.target.value)}
            className={
              isDark
                ? "border-slate-700 bg-slate-950 text-white placeholder:text-slate-500"
                : "border-slate-200 bg-white text-slate-900"
            }
            required
          />

          <Input
            type="number"
            placeholder="Open maintenance"
            value={form.maintenance_open}
            onChange={(e) => update("maintenance_open", e.target.value)}
            className={
              isDark
                ? "border-slate-700 bg-slate-950 text-white placeholder:text-slate-500"
                : "border-slate-200 bg-white text-slate-900"
            }
            required
          />

          <div className="md:col-span-2">
            <Input
              placeholder="Manager"
              value={form.manager}
              onChange={(e) => update("manager", e.target.value)}
              className={
                isDark
                  ? "border-slate-700 bg-slate-950 text-white placeholder:text-slate-500"
                  : "border-slate-200 bg-white text-slate-900"
              }
              required
            />
          </div>

          <div className="md:col-span-2">
            <label className={`mb-2 block text-sm font-medium ${isDark ? "text-slate-300" : "text-slate-700"}`}>
              Property image
            </label>
            <Input
              type="file"
              accept="image/*"
              onChange={(e) => setPropertyImageFile(e.target.files?.[0] || null)}
              className={
                isDark
                  ? "border-slate-700 bg-slate-950 text-white placeholder:text-slate-500"
                  : "border-slate-200 bg-white text-slate-900"
              }
            />
            <p className={`mt-2 text-xs ${isDark ? "text-slate-400" : "text-slate-500"}`}>
              {propertyImageFile
                ? `Ready to upload: ${propertyImageFile.name}`
                : form.property_image_url
                ? "Current image is saved. Choose a new file to replace it."
                : "Attach a property image from your device."}
            </p>
          </div>

          <div className="md:col-span-2">
            <div
              className={`rounded-2xl border px-4 py-3 text-sm ${
                isDark
                  ? "border-slate-700 bg-slate-950 text-slate-300"
                  : "border-slate-200 bg-slate-50 text-slate-600"
              }`}
            >
              Total monthly revenue is calculated automatically as:
              <span className="ml-2 font-semibold">
                {formatMoney(
                  Number(form.rent_per_unit || 0) * Number(form.occupied || 0),
                  currency
                )}
              </span>
            </div>
          </div>

          <div className="mt-2 flex items-center justify-end gap-3 md:col-span-2">
            <button
              type="button"
              onClick={onClose}
              className={`inline-flex h-12 items-center justify-center rounded-2xl border px-5 font-medium transition ${
                isDark
                  ? "border-slate-700 bg-slate-900 text-white hover:bg-slate-800"
                  : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50"
              }`}
            >
              Cancel
            </button>

            <button
              type="submit"
              disabled={saving}
              className={`inline-flex h-12 items-center justify-center rounded-2xl px-5 font-medium text-white transition ${
                saving ? "bg-orange-300" : "bg-orange-500 hover:bg-orange-600"
              }`}
            >
              {saving
                ? mode === "edit"
                  ? "Uploading..."
                  : "Saving..."
                : mode === "edit"
                ? "Update Property"
                : "Create Property"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function DeletePropertyModal({ isOpen, property, onClose, onDeleted, isDark }) {
  const toast = useToast();
  const [deleting, setDeleting] = useState(false);

  if (!isOpen || !property) return null;

  const handleDelete = async () => {
    setDeleting(true);

    const { error } = await supabase.from("properties").delete().eq("id", property.id);

    setDeleting(false);

    if (error) {
      console.error("Delete error:", error);
      toast.error("Property deletion failed", "Please try again in a moment.");
      return;
    }

    toast.success("Property deleted", `${property.name} has been removed from SpaceM8.`);
    onDeleted();
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div
        className={`w-full max-w-lg rounded-[2rem] border p-6 shadow-2xl ${
          isDark ? "border-slate-800 bg-slate-900" : "border-slate-200 bg-white"
        }`}
      >
        <h2 className={`text-2xl font-semibold ${isDark ? "text-white" : "text-slate-900"}`}>
          Delete Property
        </h2>
        <p className={`mt-2 text-sm leading-7 ${isDark ? "text-slate-400" : "text-slate-600"}`}>
          Are you sure you want to delete{" "}
          <span className={`font-semibold ${isDark ? "text-white" : "text-slate-900"}`}>
            {property.name}
          </span>
          ? This action cannot be undone.
        </p>

        <div className="mt-6 flex items-center justify-end gap-3">
          <button
            onClick={onClose}
            className={`inline-flex h-12 items-center justify-center rounded-2xl border px-5 font-medium transition ${
              isDark
                ? "border-slate-700 bg-slate-900 text-white hover:bg-slate-800"
                : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50"
            }`}
          >
            Cancel
          </button>

          <button
            onClick={handleDelete}
            disabled={deleting}
            className={`inline-flex h-12 items-center justify-center rounded-2xl px-5 font-medium text-white transition ${
              deleting ? "bg-red-300" : "bg-red-500 hover:bg-red-600"
            }`}
          >
            {deleting ? "Deleting..." : "Delete Property"}
          </button>
        </div>
      </div>
    </div>
  );
}

function PropertyProfileModal({ isOpen, property, onClose, isDark, currency }) {
  if (!isOpen || !property) return null;

  const occupancyRate =
    property.units > 0 ? Math.round((property.occupied / property.units) * 100) : 0;
  const totalRevenue =
    Number(property.rent_per_unit || 0) * Number(property.occupied || 0);

  const profileItems = [
    {
      label: "Location",
      value: property.location || "Not set",
    },
    {
      label: "Type",
      value: property.type || "Not set",
    },
    {
      label: "Manager",
      value: property.manager || "Not set",
    },
    {
      label: "Status",
      value: property.status || "Not set",
    },
    {
      label: "Monthly revenue",
      value: formatMoney(totalRevenue, currency),
    },
    {
      label: "Rent per unit",
      value: formatMoney(Number(property.rent_per_unit || 0), currency),
    },
    {
      label: "Occupancy",
      value: `${occupancyRate}%`,
    },
    {
      label: "Units",
      value: `${property.occupied || 0}/${property.units || 0}`,
    },
    {
      label: "Open maintenance",
      value: `${property.maintenance_open || 0}`,
    },
    {
      label: "Property ID",
      value: property.id || "Not set",
    },
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div
        className={`w-full max-w-3xl rounded-[2rem] border p-6 shadow-2xl ${
          isDark ? "border-slate-800 bg-slate-900" : "border-slate-200 bg-white"
        }`}
      >
        <div className="mb-6 flex items-start justify-between gap-4">
          <div className="flex min-w-0 items-center gap-4">
            <div
              className={`flex h-20 w-20 shrink-0 items-center justify-center overflow-hidden rounded-2xl border ${
                isDark ? "border-slate-700 bg-slate-800" : "border-slate-200 bg-slate-50"
              }`}
            >
              {property.property_image_url ? (
                <StorageImage
                  src={property.property_image_url}
                  alt={property.name}
                  className="h-full w-full object-cover"
                />
              ) : (
                <Image className={`h-8 w-8 ${isDark ? "text-slate-500" : "text-slate-400"}`} />
              )}
            </div>

            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <h2 className={`break-words text-2xl font-semibold ${isDark ? "text-white" : "text-slate-900"}`}>
                  {property.name}
                </h2>
                <Badge className={getStatusClasses(property.status)}>{property.status}</Badge>
              </div>
              <p className={`mt-2 text-sm ${isDark ? "text-slate-400" : "text-slate-500"}`}>
                Property profile and operating snapshot.
              </p>
            </div>
          </div>

          <button
            onClick={onClose}
            className={`rounded-2xl border p-3 transition ${
              isDark
                ? "border-slate-700 bg-slate-900 text-white hover:bg-slate-800"
                : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50"
            }`}
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="grid gap-3 md:grid-cols-2">
          {profileItems.map((item) => (
            <div
              key={item.label}
              className={`min-w-0 rounded-2xl border px-4 py-3 ${
                isDark ? "border-slate-700 bg-slate-800" : "border-slate-200 bg-slate-50"
              }`}
            >
              <p className={`text-xs uppercase tracking-wide ${isDark ? "text-slate-400" : "text-slate-500"}`}>
                {item.label}
              </p>
              <p className={`mt-1 break-words font-semibold ${isDark ? "text-white" : "text-slate-900"}`}>
                {item.value}
              </p>
            </div>
          ))}
        </div>

        <div className="mt-6 flex justify-end">
          <button
            onClick={onClose}
            className={`inline-flex h-12 items-center justify-center rounded-2xl border px-5 font-medium transition ${
              isDark
                ? "border-slate-700 bg-slate-900 text-white hover:bg-slate-800"
                : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50"
            }`}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

function PropertyCard({
  property,
  index,
  compact = false,
  currency,
  isDark,
  onViewProfile,
  onEdit,
  onManage,
  onDelete,
}) {
  const occupancyRate =
    property.units > 0 ? Math.round((property.occupied / property.units) * 100) : 0;

  const totalRevenue =
    Number(property.rent_per_unit || 0) * Number(property.occupied || 0);

  return (
    <motion.div
      initial={{ opacity: 0, y: 18 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35, delay: index * 0.05 }}
      className={`rounded-[2rem] border shadow-md transition-all hover:-translate-y-0.5 hover:shadow-lg ${
        isDark ? "border-slate-800 bg-slate-900" : "border-slate-200 bg-white"
      }`}
    >
      <div className="p-5">
        <div className="flex items-start justify-between gap-4">
          <div className="flex min-w-0 gap-4">
            <div
              className={`hidden h-16 w-16 shrink-0 items-center justify-center overflow-hidden rounded-2xl border sm:flex ${
                isDark ? "border-slate-700 bg-slate-800" : "border-slate-200 bg-slate-50"
              }`}
            >
              {property.property_image_url ? (
                <StorageImage
                  src={property.property_image_url}
                  alt={property.name}
                  className="h-full w-full object-cover"
                />
              ) : (
                <Image className={`h-6 w-6 ${isDark ? "text-slate-500" : "text-slate-400"}`} />
              )}
            </div>

            <div className="min-w-0">
            <div className="flex items-center gap-2">
              <h3 className={`text-lg font-semibold ${isDark ? "text-white" : "text-slate-900"}`}>
                {property.name}
              </h3>
              <Badge className={getStatusClasses(property.status)}>{property.status}</Badge>
            </div>

            <div
              className={`mt-2 flex flex-wrap items-center gap-3 text-sm ${
                isDark ? "text-slate-400" : "text-slate-500"
              }`}
            >
              <span className="inline-flex items-center gap-1.5">
                <MapPin className="h-4 w-4" />
                {property.location}
              </span>
              <span>{property.type}</span>
              <span>{property.id?.slice(0, 8)}</span>
            </div>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={() => onEdit(property)}
              className={`rounded-xl border p-2 transition ${
                isDark
                  ? "border-slate-700 text-slate-400 hover:bg-slate-800 hover:text-white"
                  : "border-slate-200 text-slate-500 hover:bg-slate-50 hover:text-slate-700"
              }`}
              title="Edit property"
            >
              <Pencil className="h-4 w-4" />
            </button>

            <button
              onClick={() => onDelete(property)}
              className={`rounded-xl border p-2 transition ${
                isDark
                  ? "border-slate-700 text-slate-400 hover:bg-slate-800 hover:text-red-400"
                  : "border-slate-200 text-slate-500 hover:bg-slate-50 hover:text-red-600"
              }`}
              title="Delete property"
            >
              <Trash2 className="h-4 w-4" />
            </button>
          </div>
        </div>

        <div className={`mt-5 grid gap-3 ${compact ? "grid-cols-2" : "grid-cols-2 xl:grid-cols-4"}`}>
          <div
            className={`rounded-2xl border px-4 py-3 ${
              isDark ? "border-slate-700 bg-slate-800" : "border-slate-200 bg-slate-50"
            }`}
          >
            <p className={`text-xs uppercase tracking-wide ${isDark ? "text-slate-400" : "text-slate-500"}`}>
              Total monthly revenue
            </p>
            <p className={`mt-1 font-semibold ${isDark ? "text-white" : "text-slate-900"}`}>
              {formatMoney(totalRevenue, currency)}
            </p>
          </div>

          <div
            className={`rounded-2xl border px-4 py-3 ${
              isDark ? "border-slate-700 bg-slate-800" : "border-slate-200 bg-slate-50"
            }`}
          >
            <p className={`text-xs uppercase tracking-wide ${isDark ? "text-slate-400" : "text-slate-500"}`}>
              Occupancy
            </p>
            <p className={`mt-1 font-semibold ${isDark ? "text-white" : "text-slate-900"}`}>
              {occupancyRate}%
            </p>
          </div>

          <div
            className={`rounded-2xl border px-4 py-3 ${
              isDark ? "border-slate-700 bg-slate-800" : "border-slate-200 bg-slate-50"
            }`}
          >
            <p className={`text-xs uppercase tracking-wide ${isDark ? "text-slate-400" : "text-slate-500"}`}>
              Units
            </p>
            <p className={`mt-1 font-semibold ${isDark ? "text-white" : "text-slate-900"}`}>
              {property.occupied}/{property.units}
            </p>
          </div>

          <div
            className={`rounded-2xl border px-4 py-3 ${
              isDark ? "border-slate-700 bg-slate-800" : "border-slate-200 bg-slate-50"
            }`}
          >
            <p className={`text-xs uppercase tracking-wide ${isDark ? "text-slate-400" : "text-slate-500"}`}>
              Maintenance
            </p>
            <p className={`mt-1 font-semibold ${isDark ? "text-white" : "text-slate-900"}`}>
              {property.maintenance_open} open
            </p>
          </div>
        </div>

        <div
          className={`mt-5 flex flex-col gap-3 border-t pt-4 md:flex-row md:items-center md:justify-between ${
            isDark ? "border-slate-800" : "border-slate-200"
          }`}
        >
          <div className={`text-sm ${isDark ? "text-slate-300" : "text-slate-600"}`}>
            Managed by{" "}
            <span className={`font-medium ${isDark ? "text-white" : "text-slate-900"}`}>
              {property.manager}
            </span>
          </div>

          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              onClick={() => onViewProfile(property)}
              className={`rounded-2xl px-4 py-2 text-sm ${
                isDark
                  ? "border-slate-700 bg-slate-900 text-white hover:bg-slate-800"
                  : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50"
              }`}
            >
              View profile
            </Button>

            <Button
              onClick={() => onManage(property)}
              className="rounded-2xl px-4 py-2 text-sm"
            >
              Manage property
            </Button>
          </div>
        </div>
      </div>
    </motion.div>
  );
}

export default function SpaceM8PropertiesPage({
  isDark,
  setIsDark,
  currency,
  setCurrency,
}) {
  const location = useLocation();
  const { profile, user, sessionClaims } = useAuth();
  const [runtimeAuthUser, setRuntimeAuthUser] = useState(null);
  const [runtimeProfile, setRuntimeProfile] = useState(null);
  const effectiveUser = user || runtimeAuthUser;
  const effectiveProfile = profile || runtimeProfile;
  const currentOrganizationId = extractOrganizationId(effectiveProfile, effectiveUser, sessionClaims);
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  const [viewMode, setViewMode] = useState("grid");
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);

  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [viewingProperty, setViewingProperty] = useState(null);
  const [editingProperty, setEditingProperty] = useState(null);
  const [deletingProperty, setDeletingProperty] = useState(null);

  useEffect(() => {
    let isMounted = true;

    if (user && profile) {
      setRuntimeAuthUser(null);
      setRuntimeProfile(null);
      return undefined;
    }

    const loadRuntimeUser = async () => {
      const {
        data: { user: authUser },
      } = await supabase.auth.getUser();

      if (!isMounted) return;

      setRuntimeAuthUser(authUser || null);

      if (!authUser) {
        setRuntimeProfile(null);
        return;
      }

      let nextProfile = null;

      const { data: profileById } = await supabase
        .from("user_profiles")
        .select("*")
        .eq("id", authUser.id)
        .maybeSingle();

      nextProfile = profileById || null;

      if (!nextProfile && authUser.email) {
        const { data: profileByEmail } = await supabase
          .from("user_profiles")
          .select("*")
          .eq("email", authUser.email)
          .maybeSingle();

        nextProfile = profileByEmail || null;
      }

      if (!isMounted) return;
      setRuntimeProfile(nextProfile);
    };

    loadRuntimeUser();

    return () => {
      isMounted = false;
    };
  }, [profile, user]);

  const fetchProperties = useCallback(async () => {
    setLoading(true);

    if (!currentOrganizationId) {
      setProperties([]);
      setLoading(false);
      return;
    }

    let propertyQuery = supabase
      .from("properties")
      .select("*")
      .order("created_at", { ascending: false });

    if (currentOrganizationId) {
      propertyQuery = propertyQuery.eq("organization_id", currentOrganizationId);
    }

    const { data, error } = await propertyQuery;

    if (error) {
      console.error("Error fetching properties:", error);
    } else {
      setProperties(data || []);
    }

    setLoading(false);
  }, [currentOrganizationId]);

  useEffect(() => {
    fetchProperties();
  }, [fetchProperties]);

  useEffect(() => {
    const nextState = location.state;

    if (!nextState) return;

    if (nextState.initialQuery) {
      setQuery(nextState.initialQuery);
    }

    if (nextState.initialStatusFilter) {
      setStatusFilter(nextState.initialStatusFilter);
    }

    window.history.replaceState({}, document.title);
  }, [location.state]);

  const totals = useMemo(() => {
    const totalUnits = properties.reduce((sum, item) => sum + (item.units || 0), 0);
    const totalOccupied = properties.reduce((sum, item) => sum + (item.occupied || 0), 0);
    const totalRevenue = properties.reduce(
      (sum, item) =>
        sum + Number(item.rent_per_unit || 0) * Number(item.occupied || 0),
      0
    );
    const totalMaintenance = properties.reduce(
      (sum, item) => sum + (item.maintenance_open || 0),
      0
    );

    return {
      totalUnits,
      totalOccupied,
      totalRevenue,
      totalMaintenance,
      occupancyRate: totalUnits > 0 ? Math.round((totalOccupied / totalUnits) * 100) : 0,
    };
  }, [properties]);

  const filteredProperties = useMemo(() => {
    return properties.filter((property) => {
      const matchesQuery =
        property.name?.toLowerCase().includes(query.toLowerCase()) ||
        property.location?.toLowerCase().includes(query.toLowerCase()) ||
        property.type?.toLowerCase().includes(query.toLowerCase()) ||
        property.id?.toLowerCase().includes(query.toLowerCase());

      const matchesStatus = statusFilter === "All" || property.status === statusFilter;

      return matchesQuery && matchesStatus;
    });
  }, [properties, query, statusFilter]);

  if (loading) {
    return (
      <AppLayout
        isDark={isDark}
        setIsDark={setIsDark}
        currency={currency}
        setCurrency={setCurrency}
        eyebrow="Portfolio Control"
        title="Properties"
        description="Manage your property portfolio, occupancy, revenue performance, and operational health in one executive workspace."
      >
        <div className={isDark ? "text-slate-300" : "text-slate-600"}>
          Loading properties...
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout
      isDark={isDark}
      setIsDark={setIsDark}
      currency={currency}
      setCurrency={setCurrency}
      eyebrow="Portfolio Control"
      title="Properties"
      description="Manage your property portfolio, occupancy, revenue performance, and operational health in one executive workspace."
      actions={
        <button
          onClick={() => setIsCreateOpen(true)}
          className="inline-flex h-12 items-center justify-center rounded-2xl bg-orange-500 px-5 font-medium text-white transition hover:bg-orange-600"
        >
          <Plus className="mr-2 h-4 w-4" />
          Add Property
        </button>
      }
    >
      <section className="grid gap-4 md:grid-cols-2 2xl:grid-cols-4">
        {[
          {
            label: "Portfolio revenue",
            value: formatMoney(totals.totalRevenue, currency),
            icon: Wallet,
            tone: "bg-orange-100 text-orange-700",
          },
          {
            label: "Occupancy rate",
            value: `${totals.occupancyRate}%`,
            icon: Building2,
            tone: "bg-sky-100 text-sky-700",
          },
          {
            label: "Occupied units",
            value: `${totals.totalOccupied}/${totals.totalUnits}`,
            icon: Users,
            tone: "bg-emerald-100 text-emerald-700",
          },
          {
            label: "Open maintenance",
            value: `${totals.totalMaintenance}`,
            icon: Wrench,
            tone: "bg-amber-100 text-amber-700",
          },
        ].map((item, index) => {
          const Icon = item.icon;
          return (
            <motion.div
              key={item.label}
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: index * 0.05 }}
              className={`rounded-[2rem] border p-5 shadow-md transition-shadow hover:shadow-lg ${
                isDark ? "border-slate-800 bg-slate-900" : "border-slate-200 bg-white"
              }`}
            >
              <div className="flex items-start justify-between">
                <div>
                  <p className={`text-sm ${isDark ? "text-slate-400" : "text-slate-500"}`}>
                    {item.label}
                  </p>
                  <h3 className={`mt-3 text-3xl font-semibold tracking-tight ${isDark ? "text-white" : "text-slate-900"}`}>
                    {item.value}
                  </h3>
                  <div
                    className={`mt-3 inline-flex items-center gap-1 rounded-full px-3 py-1 text-sm ${
                      isDark ? "bg-slate-800 text-slate-300" : "bg-slate-50 text-slate-600"
                    }`}
                  >
                    <ArrowUpRight className="h-4 w-4 text-sky-600" />
                    Strong portfolio trend
                  </div>
                </div>

                <div className={`rounded-2xl p-3 ${item.tone}`}>
                  <Icon className="h-6 w-6" />
                </div>
              </div>
            </motion.div>
          );
        })}
      </section>

      <section
        className={`rounded-[2rem] border p-4 shadow-sm md:p-5 ${
          isDark ? "border-slate-800 bg-slate-900" : "border-slate-200 bg-white"
        }`}
      >
        <div className="flex flex-col gap-4 xl:flex-row xl:items-center xl:justify-between">
          <div className="flex flex-1 flex-col gap-3 md:flex-row md:items-center">
            <div className="relative w-full max-w-md">
              <Search className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
              <Input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search by name, location, type, or property ID..."
                className={`h-12 pl-11 pr-4 ${
                  isDark
                    ? "border-slate-700 bg-slate-950 text-white placeholder:text-slate-500"
                    : "border-slate-200 bg-white text-slate-800 placeholder:text-slate-500"
                }`}
              />
            </div>

            <div className="flex items-center gap-2">
              {["All", "High performing", "Stable", "Needs attention"].map((status) => (
                <button
                  key={status}
                  onClick={() => setStatusFilter(status)}
                  className={`rounded-2xl px-4 py-2 text-sm font-medium transition ${
                    statusFilter === status
                      ? "bg-sky-100 text-sky-700"
                      : isDark
                      ? "bg-slate-800 text-slate-300 hover:bg-slate-700"
                      : "bg-slate-50 text-slate-600 hover:bg-slate-100"
                  }`}
                >
                  {status}
                </button>
              ))}
            </div>
          </div>

          <div className="flex items-center gap-2 self-end xl:self-auto">
            <button
              onClick={() => setViewMode("grid")}
              className={`rounded-2xl border px-4 py-3 transition ${
                viewMode === "grid"
                  ? "border-sky-200 bg-sky-50 text-sky-700"
                  : isDark
                  ? "border-slate-700 bg-slate-900 text-slate-300 hover:bg-slate-800"
                  : "border-slate-200 bg-white text-slate-600 hover:bg-slate-50"
              }`}
            >
              <Grid3X3 className="h-4 w-4" />
            </button>

            <button
              onClick={() => setViewMode("list")}
              className={`rounded-2xl border px-4 py-3 transition ${
                viewMode === "list"
                  ? "border-sky-200 bg-sky-50 text-sky-700"
                  : isDark
                  ? "border-slate-700 bg-slate-900 text-slate-300 hover:bg-slate-800"
                  : "border-slate-200 bg-white text-slate-600 hover:bg-slate-50"
              }`}
            >
              <List className="h-4 w-4" />
            </button>

            <button
              className={`rounded-2xl border px-4 py-3 transition ${
                isDark
                  ? "border-slate-700 bg-slate-900 text-slate-300 hover:bg-slate-800"
                  : "border-slate-200 bg-white text-slate-600 hover:bg-slate-50"
              }`}
            >
              <Filter className="h-4 w-4" />
            </button>
          </div>
        </div>
      </section>

      <section className={viewMode === "grid" ? "grid gap-5 xl:grid-cols-2" : "space-y-5"}>
        {filteredProperties.length === 0 ? (
          <div
            className={`rounded-3xl border p-8 text-center ${
              isDark ? "border-slate-800 bg-slate-900 text-slate-300" : "border-slate-200 bg-white text-slate-600"
            }`}
          >
            No properties found.
          </div>
        ) : (
          filteredProperties.map((property, index) => (
            <PropertyCard
              key={property.id}
              property={property}
              index={index}
              compact={viewMode === "list"}
              currency={currency}
              isDark={isDark}
              onViewProfile={setViewingProperty}
              onEdit={setEditingProperty}
              onManage={setEditingProperty}
              onDelete={setDeletingProperty}
            />
          ))
        )}
      </section>

        <PropertyFormModal
          isOpen={isCreateOpen}
          mode="create"
          onClose={() => setIsCreateOpen(false)}
          onSaved={fetchProperties}
          isDark={isDark}
          currency={currency}
          currentOrganizationId={currentOrganizationId}
        />

      <PropertyProfileModal
        isOpen={!!viewingProperty}
        property={viewingProperty}
        onClose={() => setViewingProperty(null)}
        isDark={isDark}
        currency={currency}
      />

      <PropertyFormModal
        isOpen={!!editingProperty}
        mode="edit"
          property={editingProperty}
          onClose={() => setEditingProperty(null)}
          onSaved={fetchProperties}
          isDark={isDark}
          currency={currency}
          currentOrganizationId={currentOrganizationId}
        />

      <DeletePropertyModal
        isOpen={!!deletingProperty}
        property={deletingProperty}
        onClose={() => setDeletingProperty(null)}
        onDeleted={fetchProperties}
        isDark={isDark}
      />
    </AppLayout>
  );
}

